import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import { theme } from './theme';
import { AuthProvider } from './contexts/AuthContext';
import { QueryProvider } from './contexts/QueryProvider';
import { NotificationProvider } from './contexts/NotificationContext';
import { ProtectedRoute, Layout, SimplePageLoader } from './components';
import './App.css';

// Eager load critical pages (login & dashboard)
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';

// Lazy load all other pages for code splitting
const CompanyListPage = lazy(() => import('./pages/CompanyListPage'));
const CompanyDetailPage = lazy(() => import('./pages/CompanyDetailPage'));
const CompanyForm = lazy(() => import('./components/forms/CompanyForm'));
const AgentListPage = lazy(() => import('./pages/AgentListPage'));
const AgentDetailPage = lazy(() => import('./pages/AgentDetailPage'));
const AgentForm = lazy(() => import('./components/forms/AgentForm'));
const TeamListPage = lazy(() => import('./pages/TeamListPage'));
const TeamDetailPage = lazy(() => import('./pages/TeamDetailPage'));
const TeamForm = lazy(() => import('./components/forms/TeamForm'));
const CustomerListPage = lazy(() => import('./pages/CustomerListPage'));
const CustomerDetailPage = lazy(() => import('./pages/CustomerDetailPage'));
const CustomerForm = lazy(() => import('./components/forms/CustomerForm'));
const ProductListPage = lazy(() => import('./pages/ProductListPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const ProductFormPage = lazy(() => import('./pages/ProductFormPage'));
const PolicyListPage = lazy(() => import('./pages/PolicyListPage'));
const PolicyDetailPage = lazy(() => import('./pages/PolicyDetailPage'));
const PolicyFormPage = lazy(() => import('./pages/PolicyFormPage'));
const CustomerPolicyDetailPage = lazy(() => import('./pages/CustomerPolicyDetailPage'));
const CustomerPolicyFormPage = lazy(() => import('./pages/CustomerPolicyFormPage'));
const CommissionListPage = lazy(() => import('./pages/CommissionListPage'));
const CommissionDetailPage = lazy(() => import('./pages/CommissionDetailPage'));
const QuotationListPage = lazy(() => import('./pages/QuotationListPage'));
const QuotationDetailPage = lazy(() => import('./pages/QuotationDetailPage'));
const QuotationEditPage = lazy(() => import('./pages/QuotationEditPage'));
const QuotationCreatePage = lazy(() => import('./pages/QuotationCreatePage'));
const InvoiceListPage = lazy(() => import('./pages/InvoiceListPage'));
const InvoiceDetailPage = lazy(() => import('./pages/InvoiceDetailPage'));
const ReceiptListPage = lazy(() => import('./pages/ReceiptListPage'));
const ReceiptDetailPage = lazy(() => import('./pages/ReceiptDetailPage'));
const ReceivablesPage = lazy(() => import('./pages/ReceivablesPage'));
const PayablesPage = lazy(() => import('./pages/PayablesPage'));
const CashFlowPage = lazy(() => import('./pages/CashFlowPage'));
const TransactionListPage = lazy(() => import('./pages/TransactionListPage'));
const RenewalListPage = lazy(() => import('./pages/RenewalListPage'));
const TicketListPage = lazy(() => import('./pages/TicketListPage'));
const TicketDetailPage = lazy(() => import('./pages/TicketDetailPage'));
const TicketCreatePage = lazy(() => import('./pages/TicketCreatePage'));
const SalesReportsPage = lazy(() => import('./pages/SalesReportsPage'));
const FinanceReportsPage = lazy(() => import('./pages/FinanceReportsPage'));
const OICReportsPage = lazy(() => import('./pages/OICReportsPage'));
const ManagementReportsPage = lazy(() => import('./pages/ManagementReportsPage'));
const DailyFinancialReportPage = lazy(() => import('./pages/DailyFinancialReportPage'));
const AccountsPayableV83Page = lazy(() => import('./pages/AccountsPayableV83Page'));
const AccountsReceivableV83Page = lazy(() => import('./pages/AccountsReceivableV83Page'));
const BenefitsReceivablePage = lazy(() => import('./pages/BenefitsReceivablePage'));
const BenefitsPayablePage = lazy(() => import('./pages/BenefitsPayablePage'));
const ProfitLossPage = lazy(() => import('./pages/ProfitLossPage'));
const UserListPage = lazy(() => import('./pages/UserListPage'));
const UserDetailPage = lazy(() => import('./pages/UserDetailPage'));
const UserForm = lazy(() => import('./components/forms/UserForm'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const LoadingDemoPage = lazy(() => import('./pages/LoadingDemoPage'));
const AvatarTestPage = lazy(() => import('./pages/AvatarTestPage'));

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryProvider>
        <AuthProvider>
          <NotificationProvider>
            <Router>
              <Suspense fallback={<SimplePageLoader text="กำลังโหลดหน้าเว็บ..." />}>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <DashboardPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/companies"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CompanyListPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/companies/new"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CompanyForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/companies/:id"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CompanyDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/companies/:id/edit"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CompanyForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/agents"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AgentListPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/agents/new"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AgentForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/agents/:id"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AgentDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/agents/:id/edit"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AgentForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/teams"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <TeamListPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/teams/new"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <TeamForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/teams/:id"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <TeamDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/teams/:id/edit"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <TeamForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/customers"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CustomerListPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/customers/new"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CustomerForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/customers/:id"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CustomerDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/customers/:id/edit"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CustomerForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/products"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ProductListPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/products/new"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ProductFormPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/products/:id"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ProductDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/products/:id/edit"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ProductFormPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/policies"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <PolicyListPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/policies/new"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CustomerPolicyFormPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/policies/:id/edit"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CustomerPolicyFormPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/policies/:id"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CustomerPolicyDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/quotations"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <QuotationListPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/quotations/new"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <QuotationCreatePage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/quotations/:id"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <QuotationDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/quotations/:id/edit"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <QuotationEditPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/invoices"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <InvoiceListPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/invoices/:id"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <InvoiceDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/receipts"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ReceiptListPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/receipts/:id"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ReceiptDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/commissions"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CommissionListPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/commissions/:id"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CommissionDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/finance/receivables"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ReceivablesPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/finance/payables"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <PayablesPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/finance/cash-flow"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <CashFlowPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/finance/transactions"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <TransactionListPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/finance/daily-report"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <DailyFinancialReportPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/finance/accounts-payable"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AccountsPayableV83Page />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/finance/accounts-receivable"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AccountsReceivableV83Page />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/finance/benefits-receivable"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <BenefitsReceivablePage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/finance/benefits-payable"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <BenefitsPayablePage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/finance/profit-loss"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ProfitLossPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/services/renewals"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <RenewalListPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/services/tickets"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <TicketListPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/services/tickets/new"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <TicketCreatePage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/services/tickets/:id"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <TicketDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/sales"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <SalesReportsPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/finance"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <FinanceReportsPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/oic"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <OICReportsPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/management"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ManagementReportsPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/users"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <UserListPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/users/new"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <UserForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/users/:id"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <UserDetailPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/users/:id/edit"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <UserForm />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ProfilePage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/loading-demo"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <LoadingDemoPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/avatar-test"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <AvatarTestPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </Suspense>
            </Router>
          </NotificationProvider>
        </AuthProvider>
      </QueryProvider>
    </ThemeProvider>
  );
}

export default App;
